// apps/client/src/assets/builders/surface.ts
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { getItem, type PatternSpec, type SurfaceEntry } from '@whiskerton/content';
import type { CodeBuilder } from '../service';

export interface Canvas2DLike { width: number; height: number; getContext(kind: '2d'): Pick<CanvasRenderingContext2D, 'fillStyle' | 'strokeStyle' | 'lineWidth' | 'fillRect' | 'beginPath' | 'arc' | 'moveTo' | 'lineTo' | 'stroke' | 'fill' | 'save' | 'restore' | 'translate' | 'rotate'> | null }
export type CanvasFactory = (w: number, h: number) => Canvas2DLike;
type Ctx = NonNullable<ReturnType<Canvas2DLike['getContext']>>;

const defaultCanvas: CanvasFactory = (w, h) => {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h) as unknown as Canvas2DLike;
  const c = document.createElement('canvas'); c.width = w; c.height = h; return c as unknown as Canvas2DLike;
};
const rgba = (hex: string, a: number) => { const n = parseInt(hex.slice(1), 16); return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${a})`; };
const disc = (x: Ctx, cx: number, cy: number, r: number, style: string) => { x.fillStyle = style; x.beginPath(); x.arc(cx, cy, r, 0, Math.PI * 2); x.fill(); };
function lcg(seed: number) { let n = seed; return () => { n = (n * 16807) % 2147483647; return n / 2147483647; }; }

const PAINTERS: Record<PatternSpec['kind'], (x: Ctx, s: number, p: PatternSpec) => void> = {
  plain(x, s, p) {                                                        // look-demo 'Teal carpet' speckle
    const r = lcg(7);
    for (let i = 0; i < 900; i++) { x.fillStyle = r() < 0.5 ? rgba(p.accent, 0.35) : 'rgba(255,255,255,.06)'; x.fillRect(r() * s, r() * s, s / 128, s / 128); }
  },
  stripes(x, s, p) {                                                      // 'Peach stripe'
    for (let i = 0; i < 4; i++) { x.fillStyle = p.accent; x.fillRect(i * s / 4, 0, s / 9, s); x.fillStyle = p.accent2 ?? '#FFFFFF'; x.fillRect(i * s / 4 + s / 6, 0, s / 64, s); }
  },
  dots(x, s, p) {                                                         // 'Sky dots'
    for (let i = 0; i < 4; i++) for (let j = 0; j < 4; j++) disc(x, (i + (j % 2) * 0.5) * s / 4 + s / 16, j * s / 4 + s / 8, s / 30, p.accent);
  },
  gingham(x, s, p) {                                                      // 'Mint gingham'
    x.fillStyle = rgba(p.accent, 0.35);
    for (let i = 0; i < 4; i++) { x.fillRect(i * s / 4, 0, s / 8, s); x.fillRect(0, i * s / 4, s, s / 8); }
  },
  checks(x, s, p) {
    for (let i = 0; i < 4; i++) for (let j = 0; j < 4; j++) if ((i + j) % 2) { x.fillStyle = p.accent; x.fillRect(i * s / 4, j * s / 4, s / 4, s / 4); }
  },
  diamonds(x, s, p) {                                                     // 'Honey'
    x.fillStyle = p.accent;
    for (let i = 0; i < 4; i++) for (let j = 0; j < 4; j++) {
      const cx = (i + 0.5) * s / 4, cy = (j + 0.5) * s / 4, r = s / 28;
      x.beginPath(); x.moveTo(cx, cy - r); x.lineTo(cx + r, cy); x.lineTo(cx, cy + r); x.lineTo(cx - r, cy); x.fill();
    }
  },
  sprig(x, s, p) {                                                        // 'Sprig' / 'Lilac bloom': petals = accent, leaf + centre = accent2
    x.fillStyle = 'rgba(255,255,255,.25)';
    for (let i = 0; i < 4; i++) x.fillRect(i * s / 4 + s * 0.086, 0, s * 0.078, s);
    const k = s / 256;
    for (let i = 0; i < 4; i++) for (let j = 0; j < 4; j++) {
      const cx = (i * 64 + 54) * k, cy = (j * 64 + (i % 2) * 32 + 20) * k;
      disc(x, cx - 5 * k, cy + 6 * k, 3 * k, p.accent2 ?? p.accent);
      for (let a = 0; a < 5; a++) disc(x, cx + Math.cos(a * 1.26) * 4 * k, cy + Math.sin(a * 1.26) * 4 * k, 3.2 * k, p.accent);
      disc(x, cx, cy, 2 * k, p.accent2 ?? p.accent);
    }
  },
  planks(x, s, p) {                                                       // look-demo planks()
    const tones = [p.base, p.accent, p.accent2 ?? p.accent, p.base], rh = s / 8, len = s * 160 / 512;
    for (let r = 0; r < 8; r++) for (let k = -1; k < 5; k++) {
      const x0 = k * len + (r % 2) * len / 2;
      x.fillStyle = tones[(r * 3 + k + 8) % 4]!; x.fillRect(x0, r * rh, len, rh);
      x.fillStyle = 'rgba(70, 46, 26, .2)'; x.fillRect(x0, r * rh, s / 170, rh);
    }
    x.fillStyle = 'rgba(70, 46, 26, .24)';
    for (let r = 0; r < 8; r++) x.fillRect(0, r * rh, s, s / 170);
  },
  tiles(x, s, p) {                                                        // 'Cream tiles'
    for (let i = 0; i < 4; i++) for (let j = 0; j < 4; j++) if ((i + j) % 2) { x.fillStyle = p.accent; x.fillRect(i * s / 4, j * s / 4, s / 4, s / 4); }
    x.strokeStyle = 'rgba(160, 150, 120, .35)'; x.lineWidth = s / 160;
    for (let i = 0; i <= 4; i++) { x.beginPath(); x.moveTo(i * s / 4, 0); x.lineTo(i * s / 4, s); x.moveTo(0, i * s / 4); x.lineTo(s, i * s / 4); x.stroke(); }
  },
  herringbone(x, s, p) {
    const w = s / 16, l = s / 4;
    for (let row = -1; row < 9; row++) for (let col = -1; col < 9; col++) {
      x.save(); x.translate(col * l, row * l / 2 + (col % 2) * (l / 4)); x.rotate(col % 2 ? Math.PI / 4 : -Math.PI / 4);
      x.fillStyle = (row + col) % 2 ? p.accent : p.base; x.fillRect(0, 0, l, w);
      x.fillStyle = 'rgba(70, 46, 26, .18)'; x.fillRect(0, 0, l, s / 200);
      x.restore();
    }
  },
};

export function paintPattern(spec: PatternSpec, sizePx: number, canvas: CanvasFactory = defaultCanvas): THREE.Texture {
  const c = canvas(sizePx, sizePx);
  const x = c.getContext('2d');
  if (!x) throw new Error('2D canvas unavailable');
  x.fillStyle = spec.base; x.fillRect(0, 0, sizePx, sizePx);
  PAINTERS[spec.kind](x, sizePx, spec);
  const tex = new THREE.CanvasTexture(c as unknown as HTMLCanvasElement);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(spec.scale, spec.scale);
  tex.anisotropy = 8;
  return tex;
}

/** The pocket model: wallpaper = a rolled swatch with a short unrolled tongue; floor = two stacked sample tiles. */
export function surfaceModel(entry: SurfaceEntry, paint: (spec: PatternSpec, sizePx: number) => THREE.Texture = paintPattern): THREE.Group {
  const g = new THREE.Group();
  g.name = `surface:${entry.kind}`;
  const mat = new THREE.MeshStandardMaterial({ map: paint(entry.pattern, 128), roughness: 0.9, metalness: 0 });
  if (entry.kind === 'wallpaper') {
    const roll = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.15, 0.62, 18), mat);
    roll.rotation.z = Math.PI / 2; roll.position.y = 0.15;
    const tongue = new THREE.Mesh(new THREE.PlaneGeometry(0.6, 0.34).rotateX(-Math.PI / 2), mat);
    tongue.position.set(0, 0.005, 0.3);
    g.add(roll, tongue);
  } else {
    for (let i = 0; i < 2; i++) {
      const tile = new THREE.Mesh(new RoundedBoxGeometry(0.52, 0.06, 0.52, 2, 0.02), mat);
      tile.position.set(i * 0.06, 0.03 + i * 0.065, -i * 0.05); tile.rotation.y = i * 0.18;
      g.add(tile);
    }
  }
  g.traverse((o) => { (o as THREE.Mesh).castShadow = true; });
  return g;
}
export const builder: CodeBuilder = { id: 'surface', build: (p) => surfaceModel(getItem(String(p.catalogId)) as SurfaceEntry) };
